import { ApplicationConfig, inject, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { authInterceptor, provideAuth } from 'angular-auth-oidc-client';

import { authConfig } from './core/auth/auth-config';
import { impersonationInterceptor } from './core/auth/impersonation.interceptor';
import { sessionExpiredInterceptor } from './core/auth/session-expiry';
import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { MockPartyCaseService, PARTY_CASE_SERVICE } from './features/party/party-case.service';
import { MockReviewerCaseService, REVIEWER_CASE_SERVICE } from './features/reviewer/reviewer-case.service';
import { HttpPartyCaseService } from './features/party/http-party-case.service';
import { HttpReviewerCaseService } from './features/reviewer/http-reviewer-case.service';

export function partyCaseServiceFactory() {
  return environment.reviewMode ? inject(MockPartyCaseService) : inject(HttpPartyCaseService);
}

export function reviewerCaseServiceFactory() {
  return environment.reviewMode ? inject(MockReviewerCaseService) : inject(HttpReviewerCaseService);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          prefix: 'p',
          darkModeSelector: false,
          cssLayer: { name: 'primeng', order: 'primeng' },
        },
      },
    }),
    provideNativeDateAdapter(),
    // Label always sits above the outline (never starts inside the input as placeholder text).
    // subscriptSizing 'dynamic' stops every field reserving a permanent line of space under the
    // control for a hint/error it may never show — that reserved strip is ~20px per field, which
    // dominates a dense multi-column form. The trade-off is that a field WITHOUT a hint reflows
    // when a validation error appears; fields that carry a <mat-hint> already occupy the space,
    // so they don't move. Set globally so no screen has to remember it.
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { floatLabel: 'always', subscriptSizing: 'dynamic' } },
    // impersonationInterceptor must come after authInterceptor() — see its own comment — so it
    // has the last word on the Authorization header while impersonation is active.
    // sessionExpiredInterceptor turns an API 401 into a fresh sign-in (see session-expiry.ts).
    provideHttpClient(withInterceptors([authInterceptor(), impersonationInterceptor, sessionExpiredInterceptor])),
    provideAuth(authConfig),
    { provide: PARTY_CASE_SERVICE, useFactory: partyCaseServiceFactory },
    { provide: REVIEWER_CASE_SERVICE, useFactory: reviewerCaseServiceFactory },
    // PAYMENT_REDIRECT_SERVICE is chosen by its own token factory (review, live or disabled) — see
    // paymentRedirectServiceFactory; no second provider here to drift from it.
  ],
};
